import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const IMG_CRM =
  "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=webp&auto=format,compress&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBkYXNoYm9hcmQlMjB1aSUyMHNjcmVlbnxlbnwwfHx8fDE3ODI5Njg2NTB8MA&ixlib=rb-4.1.0&q=85";
const IMG_API =
  "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?crop=entropy&cs=srgb&fm=webp&auto=format,compress&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBkYXNoYm9hcmQlMjB1aSUyMHNjcmVlbnxlbnwwfHx8fDE3ODI5Njg2NTB8MA&ixlib=rb-4.1.0&q=85";
const IMG_ECOM =
  "https://images.unsplash.com/photo-1483985988355-763728e1935b?crop=entropy&cs=srgb&fm=webp&auto=format,compress&q=85";
const IMG_PAY =
  "https://images.unsplash.com/photo-1556742044-3c52d6e88c62?crop=entropy&cs=srgb&fm=webp&auto=format,compress&q=85";

const PROJECTS = [
  {
    id: "helio",
    name: "Helio Sales OS",
    kind: "Custom CRM · Sales Automation",
    year: "2024",
    tags: ["Laravel", "React", "PostgreSQL", "AWS"],
    img: IMG_CRM,
    desc: "A B2B sales operating system replacing five disconnected tools — pipeline, quoting, contracts and analytics in one place.",
    span: "md:col-span-7",
  },
  {
    id: "atrium",
    name: "Atrium Marketplace",
    kind: "Headless eCommerce · Multi-vendor",
    year: "2024",
    tags: ["Next.js", "Node.js", "Stripe Connect"],
    img: IMG_ECOM,
    desc: "Multi-vendor marketplace with headless commerce, split payouts and a custom vendor portal.",
    span: "md:col-span-5",
  },
  {
    id: "meridian",
    name: "Meridian Pay Bridge",
    kind: "Payment Gateway · PCI-compliant",
    year: "2023",
    tags: ["Authorize.net", "NMI", "PayPal", "3DS"],
    img: IMG_PAY,
    desc: "A payment abstraction layer routing traffic across four providers with intelligent fallback and reconciliation.",
    span: "md:col-span-5",
  },
  {
    id: "vertex",
    name: "Vertex API Fabric",
    kind: "GraphQL & REST · Platform APIs",
    year: "2025",
    tags: ["Node.js", "GraphQL", "AWS Lambda"],
    img: IMG_API,
    desc: "A unified API fabric consolidating a decade-old service estate behind versioned, well-documented endpoints.",
    span: "md:col-span-7",
  },
];

function Card({ p, i }) {
  return (
    <motion.article
      data-testid={`project-card-${p.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10% 0px" }}
      transition={{ duration: 0.7, delay: i * 0.06, ease: [0.2, 0.7, 0.2, 1] }}
      className={`group relative ${p.span}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden clipped-frame bg-[#0b0e18] border border-hairline grain">
        <img
          src={p.img}
          alt={p.name}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-[1.05] transition-all duration-[800ms] ease-[cubic-bezier(.2,.7,.2,1)]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#080A0F] via-[#080A0F]/10 to-transparent" />
        <div className="absolute left-5 top-5 mono-label !text-white/80">{p.kind}</div>
        <div className="absolute right-5 top-5 mono-label !text-white/60">{p.year}</div>
      </div>

      <div className="mt-6 flex items-start justify-between gap-6">
        <div>
          <h3 className="font-display text-2xl md:text-[32px] tracking-tight leading-tight">
            {p.name}
          </h3>
          <p className="mt-3 text-white/60 text-[14px] md:text-[15px] leading-[1.6] max-w-[52ch]">
            {p.desc}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {p.tags.map((t) => (
              <span key={t} className="font-mono text-[11px] px-2.5 py-1 border border-hairline text-white/70">
                {t}
              </span>
            ))}
          </div>
        </div>
        <a
          href="#contact"
          data-testid={`project-link-${p.id}`}
          className="shrink-0 mt-1 inline-flex items-center gap-2 mono-label !text-white group-hover:!text-[color:var(--accent)] transition-colors"
        >
          Case study <ArrowUpRight size={14} />
        </a>
      </div>
    </motion.article>
  );
}

export default function Portfolio() {
  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative border-t border-hairline py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-8 mb-16 md:mb-20 items-end">
          <div className="md:col-span-3">
            <span className="mono-label">[05] Selected work</span>
          </div>
          <div className="md:col-span-9 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-15% 0px" }}
              transition={{ duration: 0.7 }}
              className="font-display text-4xl md:text-6xl lg:text-7xl tracking-[-0.03em] leading-[0.98]"
            >
              A partial record.
              <br />
              <span className="text-white/40">In production, worldwide.</span>
            </motion.h2>
            <div className="mono-label !text-white/50">
              <span className="text-white">04</span> / featured
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-8 md:gap-10">
          {PROJECTS.map((p, i) => (
            <Card key={p.id} p={p} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
